import React, { useEffect, useState } from 'react';
import { AssessmentAnswers, ServiceNeed, LandlordProfile } from './types';
import { evaluateNeeds } from './recommendationEngine';
import { useUsdPricing } from '../../../hooks/useUsdPricing';
import { displayTierName, formatUsd, UsdPlan } from '../../../services/usdPricingModel';
import { trackVariantEvent } from '../../../services/analytics';
import { getAssignedVariant } from '../../../services/experimentAssignment';

const TIER_TO_PLAN_ID: Record<string, string> = {
  Free: 'freemium',
  Essentials: 'essentials',
  Professional: 'professional',
  Enterprise: 'enterprise',
};

const formatMoney = (amount: number, currency: string): string =>
  currency === 'USD' ? formatUsd(amount) : `${amount} ${currency}`;

const planPriceLabel = (plan: UsdPlan): string => {
  if (plan.contactSales) return 'Contact sales';
  if (plan.price === 0) return 'Free';
  return `${formatMoney(plan.price, plan.currency)}/mo`;
};

const planAllowanceLabel = (plan: UsdPlan): string | null => {
  const n = plan.serviceCallAllowance;
  return typeof n === 'number' && n > 0 && n < 9999
    ? `${n} service calls/month included`
    : null;
};

export const NeedsAssessmentWizard: React.FC = () => {
  const [step, setStep] = useState<number>(1);
  const [answers, setAnswers] = useState<AssessmentAnswers>({
    propertyCount: '',
    serviceNeeds: [],
    landlordProfile: null,
  });

  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const { plans, services, loading } = useUsdPricing();

  const handleNext = () => setStep((s) => s + 1);
  const handleBack = () => setStep((s) => Math.max(1, s - 1));

  // T-164-002: the completion milestone fires when the recommendation is shown,
  // tagged with the assigned experiment variant so the significance calculator
  // (S9) can bucket completion-rate per arm. Fires once per completion.
  useEffect(() => {
    if (step === 4) {
      trackVariantEvent('assessment_completed', {
        variant: getAssignedVariant('needs-assessment'),
        recommendedTier: (
          evaluateNeeds(answers, plans).recommendedTier
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const toggleServiceNeed = (need: ServiceNeed) => {
    setAnswers((prev) => {
      const needs = prev.serviceNeeds.includes(need)
        ? prev.serviceNeeds.filter((n) => n !== need)
        : [...prev.serviceNeeds, need];
      return { ...prev, serviceNeeds: needs };
    });
  };

  const isStep1Valid = answers.propertyCount !== '' && Number(answers.propertyCount) >= 0;
  const isStep2Valid = true; // Service needs are optional
  const isStep3Valid = answers.landlordProfile !== null;

  const renderStep1 = () => (
    <div data-testid="wizard-step-1">
      <h2 className="text-xl font-bold mb-4">Step 1: Property Portfolio</h2>
      <label className="block mb-2 font-semibold">How many properties do you manage?</label>
      <input
        type="number"
        min="0"
        data-testid="property-count-input"
        className="w-full border p-2 rounded"
        value={answers.propertyCount}
        onChange={(e) => setAnswers({ ...answers, propertyCount: e.target.value ? Number(e.target.value) : '' })}
      />
      <div className="mt-6 flex justify-end">
        <button
          data-testid="next-button"
          disabled={!isStep1Valid}
          onClick={handleNext}
          className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div data-testid="wizard-step-2">
      <h2 className="text-xl font-bold mb-4">Step 2: Service Needs</h2>
      <p className="mb-4">Select the services you are interested in (optional):</p>
      <div className="space-y-2">
        {(['compliance', 'eviction', 'evidence', 'flood'] as ServiceNeed[]).map((need) => (
          <label key={need} className="flex items-center space-x-2">
            <input
              type="checkbox"
              data-testid={`service-need-${need}`}
              checked={answers.serviceNeeds.includes(need)}
              onChange={() => toggleServiceNeed(need)}
            />
            <span className="capitalize">{need}</span>
          </label>
        ))}
      </div>
      <div className="mt-6 flex justify-between">
        <button onClick={handleBack} className="bg-gray-200 px-4 py-2 rounded">Back</button>
        <button
          data-testid="next-button"
          disabled={!isStep2Valid}
          onClick={handleNext}
          className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div data-testid="wizard-step-3">
      <h2 className="text-xl font-bold mb-4">Step 3: Landlord Profile</h2>
      <p className="mb-4">Are you a property owner or a property manager?</p>
      <div className="space-y-2">
        {(['owner', 'manager'] as LandlordProfile[]).map((profile) => (
          <label key={profile} className="flex items-center space-x-2">
            <input
              type="radio"
              name="landlordProfile"
              data-testid={`profile-${profile}`}
              checked={answers.landlordProfile === profile}
              onChange={() => setAnswers({ ...answers, landlordProfile: profile })}
            />
            <span className="capitalize">{profile}</span>
          </label>
        ))}
      </div>
      <div className="mt-6 flex justify-between">
        <button onClick={handleBack} className="bg-gray-200 px-4 py-2 rounded">Back</button>
        <button
          data-testid="finish-button"
          disabled={!isStep3Valid}
          onClick={handleNext}
          className="bg-green-600 text-white px-4 py-2 rounded disabled:opacity-50"
        >
          Finish Assessment
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => {
    const result = evaluateNeeds(answers, plans);
    const plan = plans.find((p) => p.id === TIER_TO_PLAN_ID[result.recommendedTier]);
    const allowance = plan ? planAllowanceLabel(plan) : null;

    const handleEmailSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (email.includes('@')) {
        try {
          // Documented Brevo Submit Seam (T-140-003)
          // Uses a placeholder endpoint since real credentials do not exist in the repo
          await fetch('https://api.brevo.com/v3/contacts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, listIds: [2] /* Mock List ID */ }),
          });
        } catch (error) {
          console.error('Brevo submission failed', error);
        }
        setEmailSubmitted(true);
      }
    };

    return (
      <div data-testid="wizard-step-4" className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-2xl font-bold mb-2">Your Recommended Plan: {result.recommendedTier}</h2>
        <p className="text-gray-600 mb-6 font-semibold">Confidence Score: {result.confidenceScore}%</p>

        <div data-testid="plan-card" className="border border-gray-200 rounded-lg p-4 mb-6 bg-gray-50">
          <div className="flex items-baseline justify-between gap-4">
            <h3 data-testid="plan-name" className="font-bold text-lg">
              {plan ? displayTierName(plan) : result.recommendedTier}
            </h3>
            {plan && (
              <span data-testid="plan-price" className="font-semibold whitespace-nowrap">
                {planPriceLabel(plan)}
              </span>
            )}
          </div>
          {plan && (
            <ul data-testid="plan-details" className="mt-2 space-y-1 text-sm text-gray-600">
              {plan.contactSales ? (
                <li>Pricing scoped to your portfolio</li>
              ) : (
                <li>Up to {plan.maxUnits} unit{plan.maxUnits === 1 ? '' : 's'}</li>
              )}
              {allowance && <li>{allowance}</li>}
            </ul>
          )}
          {!plan && !loading && (
            <p className="mt-2 text-sm text-gray-500">
              Full plan details are unavailable right now — your recommendation still stands.
            </p>
          )}
        </div>

        <div className="mb-6">
          <h3 className="font-bold mb-2">Why this plan?</h3>
          <ul className="list-disc pl-5 space-y-1">
            {result.reasoning.map((reason, idx) => (
              <li key={idx} className="text-sm">{reason}</li>
            ))}
          </ul>
        </div>

        <div data-testid="service-catalog" className="border-t pt-6 mb-6">
          <h3 className="font-bold mb-1">Per-use services</h3>
          <p className="text-sm text-gray-600 mb-3">
            Add these any time — pay only when you use them:
          </p>
          <ul className="space-y-3">
            {services.map((service) => (
              <li
                key={service.type}
                data-testid={`service-item-${service.type}`}
                className="flex items-baseline justify-between gap-4"
              >
                <div>
                  <span className="font-semibold text-sm">{service.name}</span>
                  <span className="block text-xs text-gray-500">{service.description}</span>
                  <span className="text-xs text-gray-500">
                    Included with{' '}
                    {service.planAccess.charAt(0).toUpperCase() + service.planAccess.slice(1)}
                  </span>
                </div>
                <span
                  data-testid={`service-price-${service.type}`}
                  className="font-semibold whitespace-nowrap"
                >
                  {formatMoney(service.price, service.currency)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <h3 className="font-bold mb-2">Next Steps</h3>
          <a
            href="https://calendly.com/landlord-pro/demo"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-md font-semibold hover:bg-blue-700"
            data-testid="book-demo-cta"
            onClick={() =>
              trackVariantEvent('booking_clicked', {
                variant: getAssignedVariant('needs-assessment'),
              })
            }
          >
            Book a Demo
          </a>
        </div>

        <div className="border-t pt-6">
          <h3 className="font-bold mb-4">
            {plan?.contactSales
              ? 'Get your tailored Enterprise proposal'
              : 'Send this plan to your inbox'}
          </h3>
          {emailSubmitted ? (
            <div data-testid="email-success" className="text-green-700 font-semibold p-3 bg-green-50 rounded">
              Plan sent to {email}!
            </div>
          ) : (
            <form onSubmit={handleEmailSubmit} data-testid="email-capture-form" className="flex gap-2">
              <input
                type="email"
                required
                data-testid="email-input"
                placeholder="Enter your email"
                className="flex-1 border p-2 rounded"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button
                type="submit"
                className="bg-gray-800 text-white px-4 py-2 rounded font-semibold hover:bg-gray-900"
              >
                Send me this plan
              </button>
            </form>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-2xl mx-auto p-4">
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
      {step === 4 && renderStep4()}
    </div>
  );
};
